import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { CheckCircle, Computer, DesktopWindows, LocationOn, Schedule } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { qrLoginService } from '../services/api';
import { useApp } from '../contexts/AppContext';

type Phase = 'loading' | 'pending' | 'approved' | 'denied' | 'done_denied' | 'invalid' | 'error';

const friendlyUserAgent = (ua?: string | null): string => {
  if (!ua) return 'Dispositivo sconosciuto';
  const browser = /Edg/.test(ua) ? 'Edge'
    : /Chrome/.test(ua) ? 'Chrome'
    : /Firefox/.test(ua) ? 'Firefox'
    : /Safari/.test(ua) ? 'Safari'
    : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : 'Sistema sconosciuto';
  return `${browser} su ${os}`;
};

const ApproveLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useApp();
  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<{ ipAddress: string | null; userAgent: string | null; createdAt: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const publicId = new URLSearchParams(window.location.search).get('lr') || '';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!publicId) {
        setPhase('invalid');
        return;
      }
      try {
        const data = await qrLoginService.info(publicId);
        if (cancelled) return;
        setInfo({ ipAddress: data.ipAddress, userAgent: data.userAgent, createdAt: data.createdAt });
        if (data.status === 'PENDING') setPhase('pending');
        else if (data.status === 'APPROVED' || data.status === 'CONSUMED') setPhase('approved');
        else if (data.status === 'DENIED') setPhase('done_denied');
        else setPhase('invalid');
      } catch (e: any) {
        if (cancelled) return;
        setPhase(e?.response?.status === 404 ? 'invalid' : 'error');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [publicId]);

  const handleApprove = async () => {
    setBusy(true);
    try {
      await qrLoginService.approve(publicId);
      setPhase('approved');
    } catch {
      setPhase('error');
    } finally {
      setBusy(false);
    }
  };

  const handleDeny = async () => {
    setBusy(true);
    try {
      await qrLoginService.deny(publicId);
      setPhase('done_denied');
    } catch {
      setPhase('error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: 'var(--surface)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-card)',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 56, height: 56, mx: 'auto', mb: 2, borderRadius: '50%',
            display: 'grid', placeItems: 'center',
            background: 'var(--gradient-main)', color: '#fff',
          }}
        >
          <DesktopWindows />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Nuovo accesso richiesto
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
          Un dispositivo sta chiedendo di accedere al tuo account
          {state.auth.user ? ` (${state.auth.user.email})` : ''}.
        </Typography>

        {phase === 'loading' && <Box sx={{ py: 3 }}><CircularProgress size={26} /></Box>}

        {phase === 'pending' && info && (
          <>
            <Box sx={{ textAlign: 'left', mb: 2.5, borderRadius: 3, border: '1px solid var(--border-color)', p: 2 }}>
              <Row icon={<Computer fontSize="small" />} label="Dispositivo" value={friendlyUserAgent(info.userAgent)} />
              <Divider sx={{ my: 1.2, borderColor: 'var(--border-color)' }} />
              <Row icon={<LocationOn fontSize="small" />} label="Indirizzo IP" value={info.ipAddress || 'n/d'} />
              <Divider sx={{ my: 1.2, borderColor: 'var(--border-color)' }} />
              <Row icon={<Schedule fontSize="small" />} label="Ora richiesta" value={new Date(info.createdAt).toLocaleString('it-IT')} />
            </Box>

            <Alert severity="info" sx={{ textAlign: 'left', mb: 2.5 }}>
              Approva solo se sei tu a effettuare l'accesso da questo dispositivo.
            </Alert>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button fullWidth variant="outlined" color="inherit" disabled={busy} onClick={handleDeny}>
                Rifiuta
              </Button>
              <Button fullWidth variant="contained" disabled={busy} onClick={handleApprove}>
                {busy ? <CircularProgress size={20} color="inherit" /> : 'Approva accesso'}
              </Button>
            </Box>
          </>
        )}

        {phase === 'approved' && (
          <>
            <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Accesso approvato
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Torna al PC: l'accesso è stato completato.
            </Typography>
          </>
        )}

        {phase === 'done_denied' && (
          <Alert severity="warning" sx={{ textAlign: 'left' }}>
            Hai rifiutato la richiesta di accesso.
          </Alert>
        )}
        {phase === 'invalid' && (
          <Alert severity="error" sx={{ textAlign: 'left' }}>
            Richiesta non valida o scaduta. Genera un nuovo QR dal PC.
          </Alert>
        )}
        {phase === 'error' && (
          <Alert severity="error" sx={{ textAlign: 'left' }}>
            Si è verificato un errore. Riprova.
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Button variant="text" onClick={() => navigate('/dashboard')} sx={{ color: 'var(--text-muted)' }}>
            Vai alla dashboard
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const Row: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
    <Box sx={{ color: 'var(--accent-blue)', display: 'flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'block', lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  </Box>
);

export default ApproveLoginPage;
